import { useState, useCallback } from "react";

export type OperationType =
  | "idle"
  | "saving"
  | "downloading"
  | "downloadingAll";

export function useOperationQueue({
  templatesData,
  setDrawAllImages,
  setActiveSlideIndex,
  setCurrentDownloadIndex,
  setToastMessage,
  setShowToast,
}: {
  templatesData: any[];
  setDrawAllImages: (val: boolean) => void;
  setActiveSlideIndex: (index: number) => void;
  setCurrentDownloadIndex: (index: number) => void;
  setToastMessage: (msg: string) => void;
  setShowToast: (val: boolean) => void;
}) {
  const [operationQueue, setOperationQueue] = useState<OperationType[]>([]);
  const [currentOperation, setCurrentOperation] =
    useState<OperationType>("idle");

  const processNextOperation = useCallback(() => {
    setOperationQueue((prevQueue) => {
      if (prevQueue.length === 0) return prevQueue;

      const next = prevQueue[0];
      const rest = prevQueue.slice(1);

      setCurrentOperation(next);

      if (next === "downloadingAll") {
        setActiveSlideIndex(0);
        setCurrentDownloadIndex(0);
      }

      setTimeout(() => {
        setDrawAllImages(true);
      }, 100);

      switch (next) {
        case "saving":
          setToastMessage("✅ Saving edits...");
          break;
        case "downloading":
          setToastMessage("✅ Downloading image...");
          break;
        case "downloadingAll":
          setToastMessage(`Downloading image 1 of ${templatesData.length}...`);
          break;
      }
      setShowToast(true);
      return rest;
    });
  }, [
    setActiveSlideIndex,
    setCurrentDownloadIndex,
    setDrawAllImages,
    setToastMessage,
    setShowToast,
    templatesData.length,
  ]);

  const queueOperation = useCallback(
    (operation: OperationType) => {
      if (operation === "idle") return;

      setOperationQueue((prev) => [...prev, operation]);

      if (currentOperation === "idle") {
        processNextOperation();
      } else {
        setToastMessage(
          "Operation queued - will run after current task completes",
        );
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);
      }
    },
    [currentOperation, processNextOperation, setToastMessage, setShowToast],
  );

  const handleImageGenerated = useCallback(
    (dataUrl: string, activeIndex: number) => {
      switch (currentOperation) {
        case "downloading":
        case "downloadingAll": {
          const link = document.createElement("a");
          link.href = dataUrl;
          const now = new Date();
          const timestamp = now
            .toISOString()
            .replace(/T/, "_")
            .replace(/:/g, "-")
            .replace(/\..+/, "");
          link.download = `before-after-image-${timestamp}-${activeIndex + 1}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          if (
            currentOperation === "downloadingAll" &&
            activeIndex < templatesData.length - 1
          ) {
            const nextIndex = activeIndex + 1;
            setActiveSlideIndex(nextIndex);
            setCurrentDownloadIndex(nextIndex);
            setTimeout(() => setDrawAllImages(true), 300);
            setToastMessage(
              `Downloading image ${nextIndex + 1} of ${templatesData.length}...`,
            );
            setShowToast(true);
          } else {
            setCurrentOperation("idle");
            setToastMessage(
              currentOperation === "downloading"
                ? "✅ Image downloaded"
                : `✅ All ${templatesData.length} images downloaded`,
            );
            setShowToast(true);
            setTimeout(() => setShowToast(false), 2000);
            setTimeout(() => processNextOperation(), 300);
          }
          break;
        }
        case "saving": {
          setCurrentOperation("idle");
          setToastMessage("✅ Edits saved");
          setShowToast(true);
          setTimeout(() => setShowToast(false), 2000);
          setTimeout(() => processNextOperation(), 300);
          break;
        }
        default: {
          setCurrentOperation("idle");
          setTimeout(() => processNextOperation(), 300);
          break;
        }
      }
    },
    [
      currentOperation,
      setActiveSlideIndex,
      setCurrentDownloadIndex,
      setDrawAllImages,
      setToastMessage,
      setShowToast,
      templatesData.length,
      processNextOperation,
    ],
  );

  const handleOperationError = useCallback(
    (message?: string) => {
      setCurrentOperation("idle");
      setToastMessage(
        message || "⚠️ Export failed. Please try downloading again.",
      );
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4500);
      setTimeout(() => processNextOperation(), 300);
    },
    [processNextOperation, setToastMessage, setShowToast],
  );

  return {
    currentOperation,
    queueOperation,
    handleImageGenerated,
    handleOperationError,
  };
}
